import React, { createContext, useContext, useRef, useEffect, useCallback, useMemo, useState } from 'react';
import { AxpKit } from '@axons-library/portal-js-sdk';
import { useDispatch, useSelector, useStore } from 'react-redux';
import { setState as setAuthState } from '../redux/authSlice';
import useExchangeToken from './useExchangeToken';
import useCheckToken from './useCheckToken';

const isIOSWebKit = () =>
  typeof navigator !== 'undefined' &&
  /iP(hone|ad|od)/i.test(navigator.userAgent) &&
  /AppleWebKit/i.test(navigator.userAgent);

const canUseLocalStorage = () => {
  try { const k = '__ls_probe__'; window.localStorage.setItem(k, '1'); window.localStorage.removeItem(k); return true; }
  catch { return false; }
};

const decideCandidates = () => {
  const preferLS = canUseLocalStorage();
  if (isIOSWebKit()) {
    return preferLS ? ['localStorage','cookie'] : ['cookie','localStorage'];
  }
  return preferLS ? ['localStorage','cookie'] : ['cookie','localStorage'];
};

const buildReturnUrl = () => (typeof window === 'undefined' ? undefined : window.location.href);

const stripQuery = (param = 'sessionKey') => {
  try {
    const url = new URL(window.location.href);
    if (url.searchParams.has(param)) {
      url.searchParams.delete(param);
      window.history.replaceState({}, '', url.toString());
    }
  } catch {}
};

const looksLikeStorageError = (e) => {
  const msg = String(e?.message || e || '').toLowerCase();
  return (
    msg.includes('not implemented') ||
    msg.includes('localstorage') ||
    msg.includes('storage') ||
    msg.includes('domexception') ||
    msg.includes('security') ||
    msg.includes('quota')
  );
};

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const dispatch = useDispatch();
  const store = useStore();
  const auth = useSelector(s => s.auth);
  const exchangeToken = useExchangeToken();
  useCheckToken();

  const kitRef = useRef(null);
  const redirectingRef = useRef(false);
  const subscribedRef = useRef(false);
  const initDoneRef = useRef(false);
  const ssoTriedRef = useRef(false);
  const seededOnceRef = useRef(false);

  const graceTimerRef = useRef(null);
  const SAFARI_FIRST_LOAD_MS = 1200;
  const [inGrace, setInGrace] = useState(true);

  const [initialSessionKey] = useState(() => {
    try { return new URLSearchParams(window.location.search).get('sessionKey') || ''; }
    catch { return ''; }
  });

  const loginWithRedirect = useCallback(() => {
    if (!kitRef.current || redirectingRef.current) return;
    redirectingRef.current = true;
    try {
      kitRef.current.loginWithRedirectUrl(buildReturnUrl());
    } catch (e) {
      console.error('[Auth] loginWithRedirect error:', e);
      redirectingRef.current = false;
    }
  }, []);

  const logout = useCallback(async () => {
    const kit = kitRef.current;
    if (!kit) return;
    try { await kit.logout(kit.getCurrentTenantUrl()); }
    catch (e) { console.error('[Auth] logout error:', e); }
  }, []);

  const onAuthStateChanged = useCallback((authState) => {
    const activeSession = authState?.sessions?.find(s => s.sessionKey === authState?.activeSessionKey) || null;
    const previous = store.getState().auth;
    const sessionChanged = previous.sessionKey !== (activeSession?.sessionKey || '');

    dispatch(setAuthState({
      activeSession: activeSession ? { sessionKey: activeSession.sessionKey, state: activeSession.state } : null,
      sessionKey: activeSession?.sessionKey || '',
      display: activeSession?.displayName || '',
      accessToken: activeSession?.accessToken || '',
      refreshToken: activeSession?.refreshToken || '',
      ...(sessionChanged || !activeSession ? {
        access_token: '', refresh_token: '', rb_ac_exp: 0, rb_rf_exp: 0,
        id: null, email: '', is_superuser: false, group: '', user_role: '',
        plant_id: '', factory: '', line: '', bu: '', hasSeededProfile: false,
      } : {}),
      isLogin: activeSession?.state === 'valid',
      loading: false,
      error: null,
    }));

    if (!activeSession || activeSession.state !== 'valid') return;

    redirectingRef.current = false;
    if (!seededOnceRef.current) {
      seededOnceRef.current = true;
      dispatch(setAuthState({ hasSeededProfile: true }));
    }
    exchangeToken?.();
  }, [dispatch, exchangeToken, store]);

  const safeLoadKit = useCallback(async () => {
    const appId = 'mes-prod';
    const baseUrl = 'https://api.axonscloud.com';
    // const appId   = 'mes-uat';
    // const baseUrl = 'https://api.uat.axonscloud.com';
    const defaultTenant = 'cpf';

    const candidates = decideCandidates();
    let lastErr = null;

    for (const storage of candidates) {
      try {
        const kit = AxpKit.load(appId, { baseUrl, storage, defaultTenant });
        kitRef.current = kit;

        if (!subscribedRef.current && kit.onActiveAuthStateChanged) {
          kit.onActiveAuthStateChanged(onAuthStateChanged);
          subscribedRef.current = true;
        }

        await kit.init();
        console.log(`[Auth] AxpKit ready with storage=${storage}`);

        if (kit.getActiveAuthState) {
          try {
            const current = await kit.getActiveAuthState();
            if (current) onAuthStateChanged(current);
          } catch (e) {
            console.warn('[Auth] getActiveAuthState failed:', e);
          }
        }

        return kit;
      } catch (e) {
        lastErr = e;
        console.warn(`[Auth] load/init failed with storage=${storage}`, e);
        if (!looksLikeStorageError(e)) break;
      }
    }

    throw lastErr || new Error('AxpKit load/init failed');
  }, [onAuthStateChanged]);

  const init = useCallback(async () => {
    if (initDoneRef.current) return;
    initDoneRef.current = true;

    try {
      await safeLoadKit();
    } catch (e) {
      console.error('[Auth] AxpKit bootstrap failed:', e);
      dispatch(setAuthState({ loading: false, error: String(e?.message || e || 'init failed') }));
      try { kitRef.current?.loginWithRedirectUrl(buildReturnUrl()); } catch {}
      return;
    }

    if (initialSessionKey && !ssoTriedRef.current) {
      ssoTriedRef.current = true;
      try {
        dispatch(setAuthState({ loading: true }));
        await kitRef.current.ssoSilent(initialSessionKey);
      } catch (e) {
        console.warn('[Auth] ssoSilent initialSessionKey failed:', e);
      } finally {
        dispatch(setAuthState({ loading: false }));
        stripQuery('sessionKey');
      }
    }

    if (graceTimerRef.current) clearTimeout(graceTimerRef.current);
    setInGrace(true);
    graceTimerRef.current = setTimeout(() => setInGrace(false), SAFARI_FIRST_LOAD_MS);
  }, [safeLoadKit, dispatch, initialSessionKey]);

  useEffect(() => {
    if (inGrace) return;
    if (!auth.isLogin && !redirectingRef.current) {
      loginWithRedirect();
    }
  }, [inGrace, auth.isLogin, loginWithRedirect]);

  useEffect(() => {
    if (auth.isLogin && !auth.access_token) {
      exchangeToken?.();
    }
  }, [auth.isLogin, auth.access_token, exchangeToken]);

  useEffect(() => { init(); }, [init]);

  useEffect(() => () => {
    if (graceTimerRef.current) clearTimeout(graceTimerRef.current);
  }, []);

  const value = useMemo(() => ({
    ...auth,
    kit: kitRef.current,
    getKit: () => kitRef.current,
    init,
    loginWithRedirect,
    logout,
    silentLogin: async (sessionKey) => {
      const kit = kitRef.current;
      if (!kit || !sessionKey) return;
      dispatch(setAuthState({ loading: true }));
      try { await kit.ssoSilent(sessionKey); }
      catch (e) { console.error('[Auth] ssoSilent', e); }
      finally {
        dispatch(setAuthState({ loading: false }));
        stripQuery('sessionKey');
      }
    },
  }), [auth, init, loginWithRedirect, logout]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = () => useContext(AuthContext);
